import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSignature, Upload, Download, Trash2, Search, CheckCircle2, AlertCircle, FileText, Calendar,
} from "lucide-react";

// Carga el archivo PDF y lo convierte a base64 (sin el prefijo data:...)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ============================================================================
// UPLOAD MODAL
// ============================================================================
function UploadModal({ empleado, existing, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [fechaFirma, setFechaFirma] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Selecciona un archivo PDF"); return; }
    if (file.type !== "application/pdf") { toast.error("El archivo debe ser PDF"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("El archivo supera 20 MB"); return; }
    setSaving(true);
    try {
      const pdf_base64 = await fileToBase64(file);
      await api.post("/contratos", {
        empleado_id: empleado.id,
        file_name: file.name,
        pdf_base64,
        fecha_firma: fechaFirma || null,
        notas: notas.trim() || null,
      });
      toast.success(existing ? "Contrato reemplazado" : "Contrato adjuntado");
      onSaved?.();
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 413) {
        toast.error("El PDF es demasiado grande para el servidor. Intentá con un archivo más liviano (< 10 MB) o comprimí el PDF.");
      } else if (detail && typeof detail === "string") {
        toast.error(detail);
      } else if (e?.message === "Network Error") {
        toast.error("Error de red. Verificá tu conexión e intentá de nuevo.");
      } else {
        toast.error("No se pudo guardar el contrato. Intentá con un PDF más pequeño o revisá la conexión.");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="contrato-upload-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
            <FileSignature size={20} strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">{existing ? "Reemplazar contrato" : "Adjuntar contrato"}</h3>
            <p className="text-xs text-secondary-c mt-0.5">{empleado.nombre} {empleado.apellidos}</p>
          </div>
        </div>

        {existing && (
          <div className="flex items-start gap-2 p-3 mb-4 rounded-xl bg-[#FFF4E5] border border-c text-xs text-secondary-c">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-warning" />
            <div>Este empleado ya tiene un contrato adjunto (<strong>{existing.file_name}</strong>). El nuevo lo reemplazará.</div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">Archivo PDF *</label>
            <input
              ref={inputRef} type="file" accept="application/pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="hidden" data-testid="contrato-file-input"
            />
            <button
              type="button" onClick={() => inputRef.current?.click()}
              className="w-full p-3 rounded-xl border-2 border-dashed border-c hover:border-primary-c hover:bg-[#FAFAF7] transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="contrato-file-picker"
            >
              <Upload size={16} />
              {file ? <span className="font-medium text-primary-c truncate">{file.name}</span> : <span className="text-secondary-c">Seleccionar PDF (máx. 20 MB)</span>}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1.5">Fecha de firma</label>
              <input type="date" value={fechaFirma} onChange={e => setFechaFirma(e.target.value)} className="np-input w-full" data-testid="contrato-fecha-firma" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">Notas</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} className="np-input w-full min-h-[70px] resize-y" data-testid="contrato-notas"
              placeholder="Tipo de contrato, vigencia, observaciones..." />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="np-btn np-btn-outline" data-testid="contrato-upload-cancel">Cancelar</button>
          <button onClick={submit} disabled={saving} className="np-btn np-btn-primary" data-testid="contrato-upload-save">
            <Upload size={14} className="mr-1.5" /> {saving ? "Guardando..." : (existing ? "Reemplazar" : "Adjuntar")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LIST
// ============================================================================
export default function Contratos() {
  const [empleados, setEmpleados] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sin"); // sin | con
  const [q, setQ] = useState("");
  const [uploadFor, setUploadFor] = useState(null); // { emp, existing }
  const [confirmDel, setConfirmDel] = useState(null); // contrato a eliminar

  const reload = async () => {
    const [e, c] = await Promise.all([api.get("/empleados"), api.get("/contratos")]);
    setEmpleados(e.data || []);
    setContratos(c.data || []);
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
  }, []);

  const contratoMap = useMemo(() => {
    const m = {}; contratos.forEach(c => { m[c.empleado_id] = c; }); return m;
  }, [contratos]);

  const activos = useMemo(() => empleados.filter(e => e.estado === "Activo"), [empleados]);
  const sinContrato = useMemo(() => activos.filter(e => !contratoMap[e.id]), [activos, contratoMap]);
  const conContrato = useMemo(() => activos.filter(e => contratoMap[e.id]), [activos, contratoMap]);

  const lista = tab === "sin" ? sinContrato : conContrato;
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return lista;
    return lista.filter(e =>
      `${e.nombre} ${e.apellidos || ""}`.toLowerCase().includes(s) ||
      (e.cedula || "").toLowerCase().includes(s) ||
      (e.puesto || "").toLowerCase().includes(s)
    );
  }, [lista, q]);

  const descargar = async (cid) => {
    try {
      const { data } = await api.get(`/contratos/${cid}`);
      if (!data?.pdf_base64) { toast.error("Sin PDF almacenado"); return; }
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.file_name || `Contrato_${cid}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error("No se pudo descargar"); }
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/contratos/${confirmDel.id}`);
      toast.success("Contrato eliminado");
      setConfirmDel(null);
      await reload();
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <FileSignature size={26} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Contratos</h1>
            <p className="text-sm text-secondary-c">Adjuntá y archivá los contratos laborales en PDF.</p>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="inline-flex p-1 rounded-xl bg-[#FAFAF7] border border-c">
          <button onClick={() => setTab("sin")} data-testid="contratos-tab-sin"
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === "sin" ? "bg-white shadow-sm text-primary-c" : "text-secondary-c hover:text-primary-c"}`}>
            <AlertCircle size={14} /> Sin contrato <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFF4E5] text-warning">{sinContrato.length}</span>
          </button>
          <button onClick={() => setTab("con")} data-testid="contratos-tab-con"
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === "con" ? "bg-white shadow-sm text-primary-c" : "text-secondary-c hover:text-primary-c"}`}>
            <CheckCircle2 size={14} /> Con contrato <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E6EFEA] text-primary-c">{conContrato.length}</span>
          </button>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-c bg-white">
          <Search size={14} className="text-secondary-c" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, cédula o puesto..."
            className="bg-transparent outline-none text-sm py-1 w-64" data-testid="contratos-search" />
        </div>
      </div>

      {loading ? (
        <div className="np-card p-12 text-center text-secondary-c">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="np-card p-14 text-center text-secondary-c">
          {tab === "sin" ? <CheckCircle2 size={36} className="mx-auto mb-3 text-success" /> : <FileSignature size={36} className="mx-auto mb-3 opacity-60" />}
          {tab === "sin" && lista.length === 0
            ? "Todos los empleados activos tienen contrato adjunto ✨"
            : "Sin resultados."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contratos-list">
          {filtered.map(emp => {
            const con = contratoMap[emp.id];
            return (
              <div key={emp.id} className="np-card p-4" data-testid={`contrato-card-${emp.cedula}`}>
                <div className="flex items-center gap-3 mb-3">
                  {emp.avatar ? (
                    <img src={emp.avatar} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      {initials(`${emp.nombre} ${emp.apellidos}`)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{emp.nombre} {emp.apellidos}</div>
                    <div className="text-xs text-secondary-c truncate">{emp.puesto || "—"}</div>
                    <div className="text-[11px] text-secondary-c">{emp.cedula}</div>
                  </div>
                </div>
                {con ? (
                  <>
                    <div className="text-xs space-y-1 mb-3 p-2.5 rounded-lg bg-[#FAFAF7] border border-c">
                      <div className="flex items-center gap-1.5 text-primary-c font-medium"><FileText size={12} /> <span className="truncate">{con.file_name}</span></div>
                      {con.fecha_firma && <div className="flex items-center gap-1.5 text-secondary-c"><Calendar size={11} /> Firmado {fmtFecha(con.fecha_firma)}</div>}
                      {con.notas && <div className="text-secondary-c italic line-clamp-2">{con.notas}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => descargar(con.id)} className="np-btn np-btn-outline flex-1 justify-center text-sm" data-testid={`contrato-download-${emp.cedula}`}>
                        <Download size={13} className="mr-1" /> Descargar
                      </button>
                      <button onClick={() => setUploadFor({ emp, existing: con })} className="np-btn np-btn-ghost p-2" title="Reemplazar" data-testid={`contrato-replace-${emp.cedula}`}><Upload size={13} /></button>
                      <button onClick={() => setConfirmDel(con)} className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]" title="Eliminar" data-testid={`contrato-delete-${emp.cedula}`}><Trash2 size={13} /></button>
                    </div>
                  </>
                ) : (
                  <button onClick={() => setUploadFor({ emp, existing: null })} className="np-btn np-btn-primary w-full justify-center text-sm" data-testid={`contrato-attach-${emp.cedula}`}>
                    <Upload size={13} className="mr-1.5" /> Adjuntar contrato
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {uploadFor && (
        <UploadModal
          empleado={uploadFor.emp}
          existing={uploadFor.existing}
          onClose={() => setUploadFor(null)}
          onSaved={async () => { setUploadFor(null); await reload(); }}
        />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="np-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Eliminar contrato</h3>
            <p className="text-sm text-secondary-c mb-4">El empleado quedará marcado como <strong>sin contrato</strong>.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="np-btn np-btn-outline" data-testid="contrato-confirm-cancel">Cancelar</button>
              <button onClick={eliminar} className="np-btn np-btn-primary !bg-error" data-testid="contrato-confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
